import { FC, JSX, useEffect, useRef, useState } from 'react';
import './ChatsList.scss';
import MasterPage from '../../../pages/MasterPage/MasterPage';
import PageBase from '../../../components/PageBase/PageBase';
import SkeletonTable from '../../../components/SkeletonTable/SkeletonTable';
import AppPagination from '../../../components/AppPagination/AppPagination';
import { toast } from 'react-toastify';
import DropdownMenuTable from '../../../components/DropdownMenuTable/DropdownMenuTable';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faSort, faTrash } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { usePaginate } from '../../../providers/PaginateProvider';
import { useChatsHistory } from '../../../providers/Chats/ChatsHistoryProvider';
import { ChatModel } from '../../../models/Chat/ChatModel';
import moment from 'moment';
import { ChatsStatusEnum } from '../../../enums/Chat/ChatStatusEnum';
import SidebarChatsListFilter from '../../../components/Chats/ChatsList/SidebarChatsListFilter/SidebarChatsListFilter';
import { useSidebarFilter } from '../../../providers/SidebarFilterProvider';
import ModalChatView from '../../../components/Chats/ChatsList/ModalChatView/ModalChatView';
import ModalChatMessageView from '../../../components/Chats/ChatsList/ModalChatMessageView/ModalChatMessageView';

interface ChatsListProps { }

type SortOption = 'name_asc' | 'name_desc' | 'messages_desc' | 'messages_asc' | null;

const SORT_LABELS: Record<NonNullable<SortOption>, string> = {
  name_asc: 'Alfabética (A-Z)',
  name_desc: 'Alfabética (Z-A)',
  messages_desc: 'Mensagens (maior → menor)',
  messages_asc: 'Mensagens (menor → maior)',
};

const ChatsList: FC<ChatsListProps> = () => {
  const [showModalView, setShowModalView] = useState<boolean>(false);
  const [sortOption, setSortOption] = useState<SortOption>(null);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortMenuPos, setSortMenuPos] = useState({ top: 0, left: 0 });
  const sortBtnRef = useRef<HTMLButtonElement>(null);

  const {
    pages,
    params,
    setParams,
    paramsMessages,
    setParamsMessages,
    isLoading,
    error,
    chats,
    setChat,
    showModalMessageView,
    setShowModalMessageView,
    selectedMessage,
    handleDelete
  } = useChatsHistory();
  const { handlePaginate } = usePaginate();
  const { showSidebarFilter, setShowSidebarFilter } = useSidebarFilter();

  const handleSort = (option: SortOption) => {
    setSortOption(option);
    setShowSortMenu(false);
    setParams((prev: any) => {
      const filter = { ...(prev?.filter ?? {}) };
      delete filter.order_name;
      delete filter.order_message;
      if (option === 'name_asc') filter.order_name = 'asc';
      else if (option === 'name_desc') filter.order_name = 'desc';
      else if (option === 'messages_desc') filter.order_message = 'desc';
      else if (option === 'messages_asc') filter.order_message = 'asc';
      return { ...prev, filter, page: 1 };
    });
  };

  const toggleSortMenu = () => {
    if (!showSortMenu && sortBtnRef.current) {
      const rect = sortBtnRef.current.getBoundingClientRect();
      setSortMenuPos({ top: rect.bottom + 4, left: rect.left });
    }
    setShowSortMenu(prev => !prev);
  };

  useEffect(() => {
    if (!showSortMenu) return;
    const close = () => setShowSortMenu(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showSortMenu]);

  const renderSortDropdown = () => (
    <div style={{ position: 'relative' }}>
      <button
        ref={sortBtnRef}
        type="button"
        className="btn btn-default-filters btn-sm mb-0"
        onClick={(e) => { e.stopPropagation(); toggleSortMenu(); }}
      >
        <span>{sortOption ? SORT_LABELS[sortOption] : 'Ordenar'}</span>
        <FontAwesomeIcon icon={faSort} className="ms-1" />
      </button>
      {showSortMenu && (
        <ul
          className="dropdown-menu show"
          style={{ position: 'fixed', top: sortMenuPos.top, left: sortMenuPos.left, zIndex: 1050, backgroundColor: '#f8f9fa' }}
          onClick={e => e.stopPropagation()}
        >
          {(['name_asc', 'name_desc', 'messages_desc', 'messages_asc'] as NonNullable<SortOption>[]).map(opt => (
            <li key={opt}>
              <button className="dropdown-item" onClick={() => handleSort(opt)}>
                {SORT_LABELS[opt]}
              </button>
            </li>
          ))}
          {sortOption && (
            <>
              <li><hr className="dropdown-divider" /></li>
              <li>
                <button className="dropdown-item" onClick={() => handleSort(null)}>
                  Limpar ordenação
                </button>
              </li>
            </>
          )}
        </ul>
      )}
    </div>
  );

  const handleView = (item: any) => {
    setChat(item);
    setParamsMessages({ ...paramsMessages, filter: { chat_id: item.id }, page: 1 });
    setShowModalView(true);
  }

  const renderList = (): JSX.Element => {
    return (
      <>
        {chats?.length > 0 ? (
          <>
            {chats.map((x: ChatModel, key: number) => (
              <tr key={x.id} className="text-sm">
                <td className="text-center">{x.id}</td>
                <td>{x?.user?.partner_code} - {x?.user?.name}</td>
                <td className="text-center">{x?.user?.user_type?.name || '-'}</td>
                <td className="text-center">{x?.messages?.length}</td>
                <td className="text-center">
                  {x?.status?.id === ChatsStatusEnum.EM_ANDAMENTO && (
                    <div className="badge badge-primary">{x?.status?.name}</div>
                  )}
                  {x?.status?.id === ChatsStatusEnum.FINALIZADO && (
                    <div className="badge badge-success">{x?.status?.name}</div>
                  )}
                </td>
                <td className="text-center">{moment(x?.created_at).format('DD/MM/YYYY HH:mm:ss')}</td>
                <td className="text-center">
                  <DropdownMenuTable key={key}>
                    <Dropdown.Item className="border-radius-md py-2" onClick={() => handleView(x)}>
                      <FontAwesomeIcon icon={faEye} className="me-2" /> Visualizar
                    </Dropdown.Item>
                  </DropdownMenuTable>
                </td>
              </tr>
            ))}
          </>
        ) : (
          <tr>
            <td colSpan={7} className="text-center text-sm">Não há registros</td>
          </tr>
        )}
      </>
    );
  }

  const renderContent = (): JSX.Element => {
    return (
      <>
        {!isLoading ? (
          <div>
            <div className="table-responsive">
              <table className="table table-flush m-0 w-100">
                <thead className="thead-light">
                  <tr>
                    <th className="text-uppercase opacity-7 text-center" scope="col">#</th>
                    <th className="text-uppercase opacity-7" scope="col">Usuário</th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">Tipo</th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">Qtd. Mensagens</th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">Status</th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">Data Início</th>
                    <th className="text-uppercase opacity-7 text-center" scope="col">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {renderList()}
                </tbody>
              </table>
            </div>
            {chats?.length > 0 && (
              <div>
                <nav aria-label="...">
                  <AppPagination
                    pages={pages}
                    handlePaginate={handlePaginate}
                    params={params}
                    setParams={setParams}
                    style={{ backgroundColor: '#fff' }}
                    exportDiv={
                      <p className="text-sm text-span mb-0">
                       <span style={{ margin: '0 8px' }}>|</span> Com um total de <span className="text-primary"><b>{chats.reduce((acc: number, x: ChatModel) => acc + (x?.messages?.length ?? 0), 0)}</b></span> mensagens
                      </p>
                    }
                  />
                </nav>
              </div>
            )}
          </div>
        ) : (
          <SkeletonTable />
        )}
      </>
    );
  }

  useEffect(() => {
    setParams({ ...params, page: 1, filter: {} });
  }, []);

  return (
    <MasterPage
      title="Histórico de Chats"
      icon="fas fa-file"
      footerFixed
    >
      <div className="ChatsList" data-testid="ChatsList">
        <PageBase
          title="Histórico de Chats"
          subtitle="Listagem do histórico de conversas com a IA."
          handleFilter={() => !!showSidebarFilter ? setShowSidebarFilter(false) : setShowSidebarFilter(true)}
          error={error}
          children={renderContent()}
          hasFilter
          renderBtnAux={renderSortDropdown()}
        />

        <ModalChatView
          show={showModalView}
          onClose={() => setShowModalView(false)}
        />
        <ModalChatMessageView
          show={showModalMessageView}
          onClose={() => setShowModalMessageView(false)}
          message={selectedMessage}
        />
      </div>

      <SidebarChatsListFilter />
    </MasterPage>
  );
}

export default ChatsList;
